import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Play, Clock, CheckCircle, XCircle, AlertCircle,
  Calendar, Users, BarChart3, Loader2, Database, Activity,
  ChevronDown, ChevronUp, Filter, Download, Search, ChevronLeft, ChevronRight
} from 'lucide-react';
import api from '../../services/api';

const cardStyle = { border: '1px solid rgba(99, 102, 241, 0.08)', boxShadow: '0 1px 3px rgba(99, 102, 241, 0.06)' };

const StatusBadge = ({ status }) => {
  const styles = {
    success: { bg: 'rgba(16, 185, 129, 0.08)', color: '#059669', border: '1px solid rgba(16, 185, 129, 0.15)' },
    failed: { bg: 'rgba(239, 68, 68, 0.08)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.15)' },
    running: { bg: 'rgba(99, 102, 241, 0.08)', color: '#4f46e5', border: '1px solid rgba(99, 102, 241, 0.15)' },
  };
  const s = styles[status] || styles.failed;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase" style={{ background: s.bg, color: s.color, border: s.border }}>
      {status === 'success' && <CheckCircle className="w-3 h-3" />}
      {status === 'failed' && <XCircle className="w-3 h-3" />}
      {status === 'running' && <Loader2 className="w-3 h-3 animate-spin" />}
      {status}
    </span>
  );
};

const DLRSummaryCron = () => {
  const [activeTab, setActiveTab] = useState('logs');
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState({ stats: false, logs: false, summary: false, trigger: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [triggerDate, setTriggerDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
  });
  const [dateRange, setDateRange] = useState(() => {
    const end = new Date();
    const start = new Date(); start.setDate(start.getDate() - 7);
    return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
  });
  const [filterUserId, setFilterUserId] = useState('');
  const [expandedLog, setExpandedLog] = useState(null);
  const [logSearch, setLogSearch] = useState('');
  const [logPage, setLogPage] = useState(1);
  const [logPageSize, setLogPageSize] = useState(10);
  const [summarySearch, setSummarySearch] = useState('');
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryPageSize, setSummaryPageSize] = useState(10);

  const fetchStats = useCallback(async () => {
    setLoading(prev => ({ ...prev, stats: true }));
    try {
      const res = await api.get('/master-admin/dlr-summary/status');
      if (res.data.success) setStats(res.data.stats);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(prev => ({ ...prev, stats: false }));
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(prev => ({ ...prev, logs: true }));
    try {
      const res = await api.get('/master-admin/dlr-summary/logs?limit=50');
      if (res.data.success) setLogs(res.data.logs || []);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(prev => ({ ...prev, logs: false }));
    }
  }, []);

  const fetchSummaryData = useCallback(async () => {
    setLoading(prev => ({ ...prev, summary: true }));
    try {
      let url = `/master-admin/dlr-summary/data?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      if (filterUserId) url += `&user_id=${filterUserId}`;
      const res = await api.get(url);
      if (res.data.success) setSummaryData(res.data.data || []);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    } finally {
      setLoading(prev => ({ ...prev, summary: false }));
    }
  }, [dateRange, filterUserId]);

  useEffect(() => { fetchStats(); fetchLogs(); }, [fetchStats, fetchLogs]);
  useEffect(() => { if (activeTab === 'summary') fetchSummaryData(); }, [activeTab, fetchSummaryData]);

  const handleTrigger = async () => {
    setError(''); setSuccess('');
    setLoading(prev => ({ ...prev, trigger: true }));
    try {
      const res = await api.post('/master-admin/dlr-summary/trigger', { date: triggerDate });
      if (res.data.success) {
        setSuccess(`Aggregation triggered for ${triggerDate}. Check logs for progress.`);
        setTimeout(() => { fetchStats(); fetchLogs(); }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to trigger aggregation');
    } finally {
      setLoading(prev => ({ ...prev, trigger: false }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return dateStr; }
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return dateStr; }
  };

  return (
    <div className="space-y-5 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <Database className="w-5 h-5 text-white" />
            </div>
            DLR Summary Cron
          </h1>
          <p className="text-sm text-gray-500 mt-1">Auto-aggregates delivery receipt counts per user/date into sms_report_summary</p>
        </div>
        <button onClick={() => { fetchStats(); fetchLogs(); if (activeTab === 'summary') fetchSummaryData(); }}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-gray-600 hover:bg-white transition-all"
          style={{ border: '1px solid rgba(99, 102, 241, 0.12)' }}>
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          {
            label: 'Worker Status',
            value: stats?.running ? 'Running' : 'Idle',
            icon: Activity,
            color: stats?.running ? '#d97706' : '#059669',
            bg: stats?.running ? 'rgba(245, 158, 11, 0.08)' : 'rgba(16, 185, 129, 0.08)',
          },
          {
            label: 'Total Runs',
            value: stats?.total_runs ?? '-',
            icon: BarChart3,
            color: '#4f46e5',
            bg: 'rgba(99, 102, 241, 0.08)',
          },
          {
            label: 'Last Run',
            value: stats?.last_run ? formatDate(stats.last_run) : 'Never',
            icon: Clock,
            color: '#0891b2',
            bg: 'rgba(8, 145, 178, 0.08)',
            small: true,
          },
          {
            label: 'Next Schedule',
            value: stats?.next_schedule ? formatDate(stats.next_schedule) : '-',
            icon: Calendar,
            color: '#7c3aed',
            bg: 'rgba(124, 58, 237, 0.08)',
            small: true,
          },
        ].map(card => (
          <div key={card.label} className="bg-white rounded-xl p-4" style={cardStyle}>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: card.bg }}>
                <card.icon className="w-3.5 h-3.5" style={{ color: card.color }} />
              </div>
              <span className="text-xs font-bold text-gray-400 uppercase">{card.label}</span>
            </div>
            <p className={`font-bold ${card.small ? 'text-xs' : 'text-lg'}`} style={{ color: card.color }}>{card.value}</p>
            {card.label === 'Worker Status' && stats?.last_error && (
              <p className="text-[10px] text-red-500 mt-1 truncate">{stats.last_error}</p>
            )}
          </div>
        ))}
      </div>

      {/* Manual Trigger */}
      <div className="bg-white rounded-xl p-5" style={cardStyle}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #d97706, #f59e0b)' }}>
            <Play className="w-3.5 h-3.5 text-white" />
          </div>
          <h2 className="text-base font-bold text-gray-900">Manual Trigger</h2>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3">
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase mb-1 block">Target Date</label>
            <input type="date" value={triggerDate} onChange={(e) => setTriggerDate(e.target.value)}
              className="px-3 py-2 text-sm rounded-lg focus:ring-2 focus:ring-indigo-500"
              style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
          <button onClick={handleTrigger} disabled={loading.trigger || !triggerDate}
            className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white shadow-sm hover:shadow-md transition-all disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
            {loading.trigger ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            Run Aggregation
          </button>
          <p className="text-xs text-gray-400 sm:ml-2">Counts are idempotent — re-running replaces, never duplicates.</p>
        </div>
        {success && (
          <div className="mt-3 px-3 py-2 rounded-lg flex items-center gap-2" style={{ background: 'rgba(16, 185, 129, 0.06)', border: '1px solid rgba(16, 185, 129, 0.15)' }}>
            <CheckCircle className="w-4 h-4" style={{ color: '#059669' }} />
            <span className="text-sm text-green-700">{success}</span>
          </div>
        )}
        {error && (
          <div className="mt-3 px-3 py-2 rounded-lg flex items-center gap-2" style={{ background: 'rgba(239, 68, 68, 0.06)', border: '1px solid rgba(239, 68, 68, 0.15)' }}>
            <AlertCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(99, 102, 241, 0.04)', border: '1px solid rgba(99, 102, 241, 0.08)' }}>
        {[
          { id: 'logs', label: 'Cron Logs', icon: Clock },
          { id: 'summary', label: 'Summary Data', icon: BarChart3 },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center"
            style={{
              background: activeTab === tab.id ? '#fff' : 'transparent',
              color: activeTab === tab.id ? '#4f46e5' : '#64748b',
              boxShadow: activeTab === tab.id ? '0 1px 3px rgba(99, 102, 241, 0.1)' : 'none',
              border: activeTab === tab.id ? '1px solid rgba(99, 102, 241, 0.1)' : '1px solid transparent',
            }}>
            <tab.icon className="w-4 h-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* Cron Logs Tab */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
          <div className="px-5 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.06)' }}>
            <h3 className="text-sm font-bold text-gray-800">Recent Cron Runs</h3>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" placeholder="Search logs..." value={logSearch} onChange={e => { setLogSearch(e.target.value); setLogPage(1); }}
                  className="pl-8 pr-3 py-1.5 text-xs rounded-lg w-48" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }} />
              </div>
              <span className="text-xs text-gray-400 whitespace-nowrap">{logs.length} entries</span>
            </div>
          </div>
          {loading.logs ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#6366f1' }} />
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Clock className="w-8 h-8 text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">No cron runs yet</p>
              <p className="text-xs text-gray-400 mt-1">The first run will happen at 2:00 AM or trigger manually above</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                    <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-400 uppercase">Run Time</th>
                    <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-400 uppercase">Target Date</th>
                    <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-400 uppercase">Status</th>
                    <th className="px-4 py-2.5 text-right text-[10px] font-bold text-gray-400 uppercase">Users</th>
                    <th className="px-4 py-2.5 text-right text-[10px] font-bold text-gray-400 uppercase">Rows</th>
                    <th className="px-4 py-2.5 text-right text-[10px] font-bold text-gray-400 uppercase">Duration</th>
                    <th className="px-4 py-2.5 text-left text-[10px] font-bold text-gray-400 uppercase">Triggered By</th>
                    <th className="px-4 py-2.5 text-center text-[10px] font-bold text-gray-400 uppercase"></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = logs.filter(log => {
                      if (!logSearch) return true;
                      const s = logSearch.toLowerCase();
                      return (log.target_date || '').toLowerCase().includes(s) ||
                        (log.status || '').toLowerCase().includes(s) ||
                        (log.triggered_by || '').toLowerCase().includes(s) ||
                        (log.error_message || '').toLowerCase().includes(s);
                    });
                    const totalLogPages = Math.ceil(filtered.length / logPageSize) || 1;
                    const paginated = filtered.slice((logPage - 1) * logPageSize, logPage * logPageSize);
                    return paginated.map((log, i) => (
                    <React.Fragment key={log.id || i}>
                      <tr className="hover:bg-gray-50 transition-colors" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.04)' }}>
                        <td className="px-4 py-2.5 text-xs text-gray-600">{formatDate(log.run_date || log.created_at)}</td>
                        <td className="px-4 py-2.5 text-xs font-mono font-semibold text-gray-800">{log.target_date}</td>
                        <td className="px-4 py-2.5"><StatusBadge status={log.status} /></td>
                        <td className="px-4 py-2.5 text-xs text-right font-semibold text-gray-700">{log.users_affected}</td>
                        <td className="px-4 py-2.5 text-xs text-right font-semibold text-gray-700">{log.rows_upserted}</td>
                        <td className="px-4 py-2.5 text-xs text-right font-mono" style={{ color: '#6366f1' }}>{log.duration}</td>
                        <td className="px-4 py-2.5">
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{
                            background: log.triggered_by === 'manual_api' ? 'rgba(245, 158, 11, 0.08)' : 'rgba(99, 102, 241, 0.06)',
                            color: log.triggered_by === 'manual_api' ? '#d97706' : '#6366f1',
                          }}>{log.triggered_by}</span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {log.error_message && (
                            <button onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)} className="text-gray-400 hover:text-gray-600">
                              {expandedLog === log.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </button>
                          )}
                        </td>
                      </tr>
                      {expandedLog === log.id && log.error_message && (
                        <tr>
                          <td colSpan={8} className="px-4 py-2" style={{ background: 'rgba(239, 68, 68, 0.03)' }}>
                            <p className="text-xs text-red-600 font-mono">{log.error_message}</p>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )); })()
                  }
                </tbody>
              </table>
              {/* Logs Pagination */}
              {(() => {
                const filtered = logs.filter(log => {
                  if (!logSearch) return true;
                  const s = logSearch.toLowerCase();
                  return (log.target_date || '').toLowerCase().includes(s) ||
                    (log.status || '').toLowerCase().includes(s) ||
                    (log.triggered_by || '').toLowerCase().includes(s) ||
                    (log.error_message || '').toLowerCase().includes(s);
                });
                const totalLogPages = Math.ceil(filtered.length / logPageSize) || 1;
                return (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-500">Rows:</span>
                      <select value={logPageSize} onChange={e => { setLogPageSize(parseInt(e.target.value)); setLogPage(1); }}
                        className="px-2 py-1 text-xs rounded-lg" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                        {[10, 25, 50].map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                      <span className="text-xs text-gray-400">Showing {Math.min((logPage-1)*logPageSize+1, filtered.length)}-{Math.min(logPage*logPageSize, filtered.length)} of {filtered.length}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setLogPage(p => Math.max(1, p-1))} disabled={logPage === 1}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                      <span className="text-xs text-gray-600 px-2">Page {logPage} of {totalLogPages}</span>
                      <button onClick={() => setLogPage(p => Math.min(totalLogPages, p+1))} disabled={logPage >= totalLogPages}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Summary Data Tab */}
      {activeTab === 'summary' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="bg-white rounded-xl p-4 flex flex-wrap items-end gap-3" style={cardStyle}>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-bold text-gray-500 uppercase">Filters</span>
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Start Date</label>
              <input type="date" value={dateRange.start} onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="px-2.5 py-1.5 text-xs rounded-lg" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }} />
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">End Date</label>
              <input type="date" value={dateRange.end} onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="px-2.5 py-1.5 text-xs rounded-lg" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }} />
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">User ID</label>
              <input type="number" value={filterUserId} onChange={(e) => setFilterUserId(e.target.value)}
                placeholder="All users" className="px-2.5 py-1.5 text-xs rounded-lg w-24"
                style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }} />
            </div>
            <button onClick={fetchSummaryData}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #4f46e5, #06b6d4)' }}>
              <RefreshCw className="w-3 h-3" /> Apply
            </button>
          </div>

          {/* Summary Table */}
          <div className="bg-white rounded-xl overflow-hidden" style={cardStyle}>
            <div className="px-5 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.06)' }}>
              <h3 className="text-sm font-bold text-gray-800">Summary Data</h3>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input type="text" placeholder="Search by user, date..." value={summarySearch} onChange={e => { setSummarySearch(e.target.value); setSummaryPage(1); }}
                    className="pl-8 pr-3 py-1.5 text-xs rounded-lg w-48" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }} />
                </div>
                <span className="text-xs text-gray-400 whitespace-nowrap">{summaryData.length} records</span>
              </div>
            </div>
            {loading.summary ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#6366f1' }} />
              </div>
            ) : summaryData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <BarChart3 className="w-8 h-8 text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No summary data found</p>
                <p className="text-xs text-gray-400 mt-1">Adjust date range or trigger aggregation</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ background: 'rgba(99, 102, 241, 0.03)' }}>
                        <th className="px-3 py-2.5 text-left text-[10px] font-bold text-gray-400 uppercase">Date</th>
                        <th className="px-3 py-2.5 text-left text-[10px] font-bold text-gray-400 uppercase">User</th>
                        <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-400 uppercase">Total</th>
                        <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-400 uppercase">Sent</th>
                        <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-400 uppercase" style={{ color: '#059669' }}>Delivered</th>
                        <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-400 uppercase" style={{ color: '#dc2626' }}>Failed</th>
                        <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-400 uppercase">Undelivered</th>
                        <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-400 uppercase">Pending</th>
                        <th className="px-3 py-2.5 text-right text-[10px] font-bold text-gray-400 uppercase">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filtered = summaryData.filter(row => {
                          if (!summarySearch) return true;
                          const s = summarySearch.toLowerCase();
                          return (row.username || '').toLowerCase().includes(s) ||
                            (row.date || '').toLowerCase().includes(s) ||
                            String(row.user_id || '').includes(s);
                        });
                        const paginated = filtered.slice((summaryPage - 1) * summaryPageSize, summaryPage * summaryPageSize);
                        return paginated.map((row, i) => {
                          const deliveryRate = row.total_sms > 0 ? ((row.delivered / row.total_sms) * 100).toFixed(1) : '0';
                          return (
                            <tr key={i} className="hover:bg-gray-50 transition-colors" style={{ borderBottom: '1px solid rgba(99, 102, 241, 0.04)' }}>
                              <td className="px-3 py-2.5 text-xs font-mono font-semibold text-gray-800">{row.date}</td>
                              <td className="px-3 py-2.5">
                                <div>
                                  <p className="text-xs font-semibold text-gray-800">{row.username}</p>
                                  <p className="text-[10px] text-gray-400">ID: {row.user_id}</p>
                                </div>
                              </td>
                              <td className="px-3 py-2.5 text-xs text-right font-bold text-gray-900">{row.total_sms?.toLocaleString()}</td>
                              <td className="px-3 py-2.5 text-xs text-right font-semibold text-gray-700">{row.sent?.toLocaleString()}</td>
                              <td className="px-3 py-2.5 text-right">
                                <span className="text-xs font-bold" style={{ color: '#059669' }}>{row.delivered?.toLocaleString()}</span>
                                {row.total_sms > 0 && (
                                  <span className="text-[10px] ml-1" style={{ color: parseFloat(deliveryRate) >= 80 ? '#059669' : '#d97706' }}>({deliveryRate}%)</span>
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-xs text-right font-bold" style={{ color: row.failed > 0 ? '#dc2626' : '#94a3b8' }}>{row.failed?.toLocaleString()}</td>
                              <td className="px-3 py-2.5 text-xs text-right font-semibold" style={{ color: row.undelivered > 0 ? '#d97706' : '#94a3b8' }}>{row.undelivered?.toLocaleString()}</td>
                              <td className="px-3 py-2.5 text-xs text-right font-semibold" style={{ color: row.pending > 0 ? '#0891b2' : '#94a3b8' }}>{row.pending?.toLocaleString()}</td>
                              <td className="px-3 py-2.5 text-[10px] text-right text-gray-400">{formatShortDate(row.updated_at)}</td>
                            </tr>
                          );
                        });
                      })()}
                    </tbody>
                    {summaryData.length > 0 && (
                      <tfoot>
                        <tr style={{ background: 'rgba(99, 102, 241, 0.04)', borderTop: '2px solid rgba(99, 102, 241, 0.1)' }}>
                          <td className="px-3 py-2.5 text-xs font-bold text-gray-600" colSpan={2}>Totals</td>
                          <td className="px-3 py-2.5 text-xs text-right font-bold text-gray-900">{summaryData.reduce((s, r) => s + (r.total_sms || 0), 0).toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-xs text-right font-bold text-gray-700">{summaryData.reduce((s, r) => s + (r.sent || 0), 0).toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-xs text-right font-bold" style={{ color: '#059669' }}>{summaryData.reduce((s, r) => s + (r.delivered || 0), 0).toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-xs text-right font-bold" style={{ color: '#dc2626' }}>{summaryData.reduce((s, r) => s + (r.failed || 0), 0).toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-xs text-right font-bold" style={{ color: '#d97706' }}>{summaryData.reduce((s, r) => s + (r.undelivered || 0), 0).toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-xs text-right font-bold" style={{ color: '#0891b2' }}>{summaryData.reduce((s, r) => s + (r.pending || 0), 0).toLocaleString()}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                {/* Summary Pagination */}
                {(() => {
                  const filtered = summaryData.filter(row => {
                    if (!summarySearch) return true;
                    const s = summarySearch.toLowerCase();
                    return (row.username || '').toLowerCase().includes(s) ||
                      (row.date || '').toLowerCase().includes(s) ||
                      String(row.user_id || '').includes(s);
                  });
                  const totalSummaryPages = Math.ceil(filtered.length / summaryPageSize) || 1;
                  return (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3" style={{ borderTop: '1px solid rgba(99, 102, 241, 0.06)' }}>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">Rows:</span>
                        <select value={summaryPageSize} onChange={e => { setSummaryPageSize(parseInt(e.target.value)); setSummaryPage(1); }}
                          className="px-2 py-1 text-xs rounded-lg" style={{ border: '1px solid rgba(99, 102, 241, 0.15)' }}>
                          {[10, 25, 50].map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                        <span className="text-xs text-gray-400">Showing {Math.min((summaryPage-1)*summaryPageSize+1, filtered.length)}-{Math.min(summaryPage*summaryPageSize, filtered.length)} of {filtered.length}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSummaryPage(p => Math.max(1, p-1))} disabled={summaryPage === 1}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
                        <span className="text-xs text-gray-600 px-2">Page {summaryPage} of {totalSummaryPages}</span>
                        <button onClick={() => setSummaryPage(p => Math.min(totalSummaryPages, p+1))} disabled={summaryPage >= totalSummaryPages}
                          className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DLRSummaryCron;
